import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Descriptions,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import {
  cancelInvoice,
  fetchBankReceiptUrl,
  issueInvoice,
  voidCreditNote,
  fetchInvoice,
  fetchInvoicePdf,
  updateInvoiceNumber
} from 'services/api/invoicing';
import {
  INVOICE_DOCUMENT_TYPE_LABELS,
  INVOICE_PAYMENT_METHOD_LABELS,
  INVOICE_SERVICE_TYPE_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  invoicePayUrl,
  CREDIT_SETTLEMENT_COLORS,
  CREDIT_SETTLEMENT_LABELS
} from 'constants/invoicing';
import {
  Invoice,
  InvoiceDocumentType,
  InvoiceLine,
  InvoicePaymentMethod,
  InvoiceStatus,
  CreditSettlement,
  InvoiceCreditNote
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import { money } from 'utils/price';
import MarkPaidModal from '../Invoices/components/MarkPaidModal';
import CreditNoteModal from './components/CreditNoteModal';
import { confirmAndSendInvoice } from '../sendInvoiceWithToast';

const dateOrDash = (value?: string | null, format = FULL_DATE_FORMAT) =>
  value ? dayjs(value).format(format) : '—';

const InvoiceDetails: FC = () => {
  const { publicId = '' } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { modal, message } = App.useApp();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [creditNotes, setCreditNotes] = useState<InvoiceCreditNote[]>([]);
  /**
   * What is still owed. null means the API did not say — an older build —
   * which is not the same as "nothing is owed", so the row hides rather
   * than printing a zero nobody can account for.
   */
  const [outstanding, setOutstanding] = useState<string | null>(null);
  const [canRefundViaStripe, setCanRefundViaStripe] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!publicId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchInvoice(publicId);

        if (cancelled) return;
        setInvoice(res.invoice);
        setLines(res.lines);
        setCreditNotes(res.creditNotes ?? []);
        setOutstanding(
          typeof res.outstandingAmount === 'string'
            ? res.outstandingAmount
            : null
        );
        setCanRefundViaStripe(!!res.canRefundViaStripe);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [publicId, refreshKey]);

  const onViewPdf = useCallback(async () => {
    try {
      const blob = await fetchInvoicePdf(publicId);
      setPdfUrl(URL.createObjectURL(blob));
      setPdfOpen(true);
    } catch (err) {
      showErrorMessage(err);
    }
  }, [publicId]);

  const closePdf = () => {
    setPdfOpen(false);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    setPdfUrl('');
  };

  const [isReceiptLoading, setReceiptLoading] = useState(false);

  /**
   * Open the stored bank receipt.
   *
   * The URL is signed and short-lived, so it is fetched when asked for
   * rather than held on the page: one rendered into the markup on load
   * would expire while the operator was reading, and would sit in the
   * DOM of a page that may be open for an hour.
   */
  /**
   * Has anything actually been credited? Compared as numbers because both
   * arrive as strings: "90.00" !== "90" though the money is the same, and
   * a string compare would show the row for a rounding difference that is
   * not one.
   */
  const isCredited =
    outstanding !== null &&
    !!invoice &&
    Number(outstanding) !== Number(invoice.totalAmount);

  /**
   * Void a credit note: the amount goes back onto the invoice.
   *
   * Confirmed because it moves a figure the accounts read, and the
   * confirmation names the note rather than asking "are you sure" about
   * nothing in particular.
   */
  const onVoidCreditNote = useCallback(
    (note: InvoiceCreditNote) => {
      modal.confirm({
        title: `Void credit note ${note.invoiceNumber ?? ''}?`,
        content:
          'Its amount goes back onto the invoice, which will be owed again.',
        okText: 'Void',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        onOk: async () => {
          try {
            await voidCreditNote(note.publicId);
            message.success('Credit note voided');
            reload();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [modal, message, reload]
  );

  const onViewReceipt = useCallback(async () => {
    if (!invoice) return;

    // OPENED BEFORE THE AWAIT, ON PURPOSE. A window.open() that runs after
    // one loses the user gesture, and the browser blocks it silently —
    // the operator clicks, nothing happens, and there is no error to show
    // them. So the tab is claimed while the click is still live and sent
    // somewhere once the URL arrives.
    const tab = window.open('', '_blank');

    if (tab) tab.opener = null;

    try {
      setReceiptLoading(true);
      const url = await fetchBankReceiptUrl(invoice.publicId);

      if (!url) throw new Error('No receipt is stored for this invoice.');

      if (tab) {
        tab.location.replace(url);
      } else {
        // Popups blocked entirely: better the same tab than nothing.
        window.location.href = url;
      }
    } catch (err) {
      tab?.close();
      showErrorMessage(err);
    } finally {
      setReceiptLoading(false);
    }
  }, [invoice]);

  const onCancel = useCallback(() => {
    if (!invoice) {
      return;
    }

    modal.confirm({
      title: `Cancel invoice ${invoice.invoiceNumber ?? ''}?`,
      okText: 'Cancel invoice',
      okButtonProps: { danger: true },
      cancelText: 'Close',
      onOk: async () => {
        try {
          await cancelInvoice(invoice.publicId);
          reload();
        } catch (err) {
          showErrorMessage(err);
        }
      }
    });
  }, [invoice, modal, reload]);

  const onSend = useCallback(() => {
    if (!invoice) {
      return;
    }

    confirmAndSendInvoice(String(invoice.id), {
      modal,
      message,
      invoiceNumber: invoice.invoiceNumber,
      email: invoice.patientEmail
    });
  }, [invoice, modal, message]);

  const currency = invoice?.currency;

  const lineColumns: ColumnProps<InvoiceLine>[] = useMemo(
    () => [
      {
        title: '#',
        key: 'index',
        width: 48,
        render: (_, __, index) => index + 1
      },
      {
        title: 'Type',
        dataIndex: 'lineType',
        key: 'lineType',
        render: (value: InvoiceLine['lineType']) =>
          INVOICE_SERVICE_TYPE_LABELS[value] ?? value
      },
      {
        title: 'Description',
        key: 'description',
        render: (_, row) => row.descriptionEn || row.descriptionDe || '—'
      },
      {
        title: 'Doctor',
        dataIndex: 'doctorName',
        key: 'doctorName',
        render: (value?: string | null) => value || '—'
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right',
        render: (value: string) => Number(value)
      },
      {
        title: 'Unit',
        dataIndex: 'unitAmount',
        key: 'unitAmount',
        align: 'right',
        render: (value: string) => money(value, { currency })
      },
      {
        // Shows why Unit and Line total differ (the BE applies the discount).
        title: 'Discount',
        key: 'discount',
        align: 'right',
        render: (_, row) => {
          const value = Number(row.discountValue ?? 0);

          if (!row.discountType || !value) {
            return '—';
          }

          return row.discountType === 'percent'
            ? `−${value}%`
            : `−${money(row.discountValue as string, { currency })}`;
        }
      },
      {
        title: 'Tax %',
        dataIndex: 'taxRate',
        key: 'taxRate',
        align: 'right',
        render: (value: string) => `${Number(value)}%`
      },
      {
        title: 'Line total',
        dataIndex: 'lineTotal',
        key: 'lineTotal',
        align: 'right',
        render: (value: string) => money(value, { currency })
      }
    ],
    [currency]
  );

  const isCreditNote = invoice?.documentType === InvoiceDocumentType.CreditNote;

  const docLabel = isCreditNote
    ? INVOICE_DOCUMENT_TYPE_LABELS[InvoiceDocumentType.CreditNote]
    : INVOICE_DOCUMENT_TYPE_LABELS[InvoiceDocumentType.Invoice];

  const canCancel =
    !!invoice &&
    !isCreditNote &&
    invoice.status !== InvoiceStatus.Paid &&
    invoice.status !== InvoiceStatus.Cancelled;

  // A credit note needs a real document to credit: never a draft (never
  // issued), a cancelled invoice (already void), or another credit note.
  const canCreditNote =
    !!invoice &&
    !isCreditNote &&
    (invoice.status === InvoiceStatus.Issued ||
      invoice.status === InvoiceStatus.Paid);

  // Manual mark-paid is allowed on any non-paid, non-cancelled invoice
  // (the BE permits it regardless of payment method).
  const canMarkPaid = canCancel;

  // Send/resend the invoice email — issued (PDF + pay link) or paid
  // (paid-invoice email). Drafts are rejected (400) by the BE.
  const canSend =
    !!invoice &&
    (invoice.status === InvoiceStatus.Issued ||
      invoice.status === InvoiceStatus.Paid);

  // Correct the human invoice number. Shown to every admin for now; the BE
  // still guards the endpoint with @Roles('super_admin') and answers 403,
  // which the error handler surfaces. The number is assigned at issuance, so
  // drafts are rejected by the BE either way.
  const canCorrectNumber = !!invoice && invoice.status !== InvoiceStatus.Draft;

  /**
   * A draft is the only thing that can be edited or issued.
   *
   * Once a number is assigned the document may be in the payer's hands and
   * is reported under that number, so it is corrected by reissuing rather
   * than rewritten — which is why Edit disappears instead of erroring.
   */
  const isDraft = !!invoice && invoice.status === InvoiceStatus.Draft;

  /**
   * Issue: assign the number, render the PDF, make it real.
   *
   * Confirmed because it is the step that cannot be taken back — from
   * here the invoice can only be paid, cancelled or reissued — and it is
   * worth saying that it does NOT email anyone, because the operator who
   * expects Send to be implied would otherwise wait for a mail that was
   * never sent.
   */
  const onIssue = useCallback(() => {
    if (!invoice) return;

    modal.confirm({
      title: 'Issue this invoice?',
      content:
        'It takes the next number and the PDF is rendered. After that it can only be paid, cancelled or reissued — not edited. Nothing is emailed until you press Send.',
      okText: 'Issue',
      cancelText: 'Close',
      onOk: async () => {
        try {
          await issueInvoice(invoice.publicId);
          message.success('Invoice issued');
          reload();
        } catch (err) {
          showErrorMessage(err);
        }
      }
    });
  }, [invoice, modal, message, reload]);

  const onCorrectNumber = () => {
    if (!invoice) {
      return;
    }

    let value = invoice.invoiceNumber ?? '';

    modal.confirm({
      title: 'Correct invoice number',
      content: (
        <Input
          defaultValue={value}
          maxLength={40}
          placeholder="Invoice number"
          onChange={e => {
            value = e.target.value;
          }}
        />
      ),
      okText: 'Save',
      onOk: async () => {
        const next = value.trim();

        if (!next) {
          message.error('Enter an invoice number');

          return Promise.reject(new Error('empty'));
        }

        try {
          await updateInvoiceNumber(String(invoice.id), next);
          message.success('Invoice number updated');
          reload();
        } catch (err) {
          showErrorMessage(err);

          return Promise.reject(err);
        }

        return undefined;
      }
    });
  };

  return (
    <PageWrapper
      title={`${docLabel}${
        invoice?.invoiceNumber ? ` ${invoice.invoiceNumber}` : ''
      }`}
      color="white"
      extra={[
        <Button
          key="back"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/invoicing/invoices')}
        >
          Back to invoices
        </Button>,
        ...(isDraft
          ? [
              <Button
                key="edit"
                onClick={() =>
                  navigate(`/invoicing/invoices/${invoice?.publicId}/edit`)
                }
              >
                Edit
              </Button>,
              <Button key="issue" type="primary" onClick={onIssue}>
                Issue
              </Button>
            ]
          : []),
        <Button key="pdf" onClick={onViewPdf} disabled={!invoice}>
          PDF
        </Button>,
        ...(canSend
          ? [
              <Button key="send" onClick={onSend}>
                Send
              </Button>
            ]
          : []),
        // Reissue corrects a document that already has a number: the
        // original is voided and a replacement issued. Issued only — a
        // draft is simply edited, and a paid one is corrected with a
        // credit note, which the API enforces either way.
        ...(invoice && invoice.status === InvoiceStatus.Issued
          ? [
              <Button
                key="reissue"
                onClick={() =>
                  navigate(`/invoicing/invoices/${invoice.publicId}/reissue`)
                }
              >
                Reissue
              </Button>
            ]
          : []),
        ...(canCorrectNumber
          ? [
              <Button key="correct-number" onClick={onCorrectNumber}>
                Correct number
              </Button>
            ]
          : []),
        ...(invoice &&
        !isCreditNote &&
        invoice.status === InvoiceStatus.Issued &&
        invoice.paymentMethod === InvoicePaymentMethod.Stripe
          ? [
              <Button
                key="paylink"
                onClick={() => {
                  copy(invoicePayUrl(invoice));
                  message.success('Payment link copied');
                }}
              >
                Copy pay link
              </Button>
            ]
          : []),
        ...(canMarkPaid
          ? [
              <Button key="mark" onClick={() => setMarkPaidOpen(true)}>
                Mark paid
              </Button>
            ]
          : []),
        ...(canCreditNote
          ? [
              <Button key="credit" onClick={() => setCreditNoteOpen(true)}>
                Credit note
              </Button>
            ]
          : []),
        ...(canCancel
          ? [
              <Button key="cancel" danger onClick={onCancel}>
                Cancel
              </Button>
            ]
          : [])
      ]}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : !invoice ? (
        <Typography.Text type="secondary">Invoice not found.</Typography.Text>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label={`${docLabel} #`}>
              {invoice.invoiceNumber || '— (draft)'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={INVOICE_STATUS_COLORS[invoice.status]}>
                {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ID">{invoice.id}</Descriptions.Item>
            {!!invoice.creditsInvoiceId && (
              <Descriptions.Item label="Credits invoice" span={2}>
                {/* creditsInvoiceId is the internal id; the detail route
                    takes an internal id or a publicId. */}
                <Link
                  to={`/invoicing/invoices/${invoice.creditsInvoiceId}`}
                >{`#${invoice.creditsInvoiceId}`}</Link>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Hashed ID">
              <Typography.Text copyable={{ text: invoice.publicId }}>
                {invoice.publicId}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Patient">
              {/* patientId is null when the invoice is billed to a non-user
                  customer — there is no profile to open, so show plain text. */}
              {invoice.patientId ? (
                <Link
                  to={`/access/patients/${invoice.patientId}/personal-information`}
                >
                  {invoice.patientName}
                </Link>
              ) : (
                invoice.patientName
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Patient email">
              {invoice.patientEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment method">
              {INVOICE_PAYMENT_METHOD_LABELS[invoice.paymentMethod] ??
                invoice.paymentMethod}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {invoice.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {dateOrDash(invoice.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Issued">
              {dateOrDash(invoice.issuedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Due">
              {dateOrDash(invoice.dueDate, DATE_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Paid">
              {dateOrDash(invoice.paidAt)}
            </Descriptions.Item>
            {/*
              TWO KINDS OF EVIDENCE, WITH DIFFERENT OWNERS. Stripe hosts
              its receipt and we hold only the link, so it can be an
              anchor. A bank receipt is a private document the patient
              sent us: it is fetched through a short-lived signed URL
              resolved from the invoice, which is why this is a button
              that asks rather than a link that already knows.
            */}
            {invoice.paymentMethod !== InvoicePaymentMethod.Stripe &&
            invoice.receiptFileId ? (
              <Descriptions.Item label="Receipt">
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  loading={isReceiptLoading}
                  onClick={onViewReceipt}
                >
                  View receipt
                </Button>
              </Descriptions.Item>
            ) : (
              invoice.receiptUrl && (
                <Descriptions.Item label="Receipt">
                  <Typography.Link
                    href={invoice.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {invoice.paymentMethod === InvoicePaymentMethod.Stripe
                      ? 'View Stripe receipt'
                      : 'View receipt'}
                  </Typography.Link>
                </Descriptions.Item>
              )
            )}
            {!!invoice.paymentReference && (
              <Descriptions.Item label="Payment reference">
                {invoice.paymentReference}
              </Descriptions.Item>
            )}
          </Descriptions>

          {(invoice.billingName || invoice.billingLine1) && (
            <Descriptions title="Billing" bordered size="small" column={2}>
              <Descriptions.Item label="Name">
                {invoice.billingName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {invoice.billingCompany || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="VAT ID">
                {invoice.billingVatId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {[
                  invoice.billingLine1,
                  invoice.billingLine2,
                  invoice.billingPostalCode,
                  invoice.billingCity,
                  invoice.billingState,
                  invoice.billingCountry
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Table
            dataSource={lines}
            columns={lineColumns}
            rowKey="publicId"
            pagination={false}
            size="small"
          />

          <Descriptions
            bordered
            size="small"
            column={1}
            style={{ maxWidth: 360 }}
          >
            <Descriptions.Item label="Subtotal">
              {money(invoice.subtotalAmount, { currency })}
            </Descriptions.Item>
            <Descriptions.Item label="Tax">
              {money(invoice.taxAmount, { currency })}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <strong>{money(invoice.totalAmount, { currency })}</strong>
            </Descriptions.Item>
            {/*
              BESIDE THE TOTAL, NEVER INSTEAD OF IT. An issued invoice says
              what it says under its number — that is the figure the
              accountants reconcile against — so a credit note changes what
              is OWED without changing the document. Shown only when the two
              differ, because a second identical figure reads as a mistake.
            */}
            {isCredited && (
              <Descriptions.Item label="Outstanding">
                <strong>{money(outstanding as string, { currency })}</strong>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/*
            THE LINK ONLY EXISTED ONE WAY. A credit note names its parent,
            so from the note you could always reach the invoice; from the
            invoice there was nothing, and an operator looking at a credited
            one had no way to see what had happened to it.
          */}
          {!!creditNotes.length && (
            <div>
              <Typography.Title level={5}>Credit notes</Typography.Title>
              <Table
                dataSource={creditNotes}
                rowKey="publicId"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Number',
                    dataIndex: 'invoiceNumber',
                    key: 'invoiceNumber',
                    render: (value: string | null, row) => (
                      <Typography.Link
                        onClick={() =>
                          navigate(`/invoicing/invoices/${row.publicId}`)
                        }
                      >
                        {value || '— (draft)'}
                      </Typography.Link>
                    )
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'totalAmount',
                    key: 'totalAmount',
                    // Stored negative — money coming back — and shown that
                    // way rather than flipped, so it matches the document.
                    render: (value: string) => money(value, { currency })
                  },
                  {
                    // A VOIDED NOTE IS STILL LISTED — it is history, and
                    // the number was issued — but it must not read like a
                    // live one. Without this the row kept saying "reduced
                    // the debt" for a note that had just stopped reducing
                    // anything, which is the same figure lying twice.
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (value: InvoiceStatus) => (
                      <Tag color={INVOICE_STATUS_COLORS[value]}>
                        {INVOICE_STATUS_LABELS[value] ?? value}
                      </Tag>
                    )
                  },
                  {
                    title: 'Settlement',
                    dataIndex: 'settlement',
                    key: 'settlement',
                    render: (value: CreditSettlement | null, row) =>
                      value ? (
                        <Tag
                          color={
                            row.status === InvoiceStatus.Cancelled
                              ? 'default'
                              : CREDIT_SETTLEMENT_COLORS[value]
                          }
                        >
                          {CREDIT_SETTLEMENT_LABELS[value]}
                        </Tag>
                      ) : (
                        '—'
                      )
                  },
                  {
                    title: 'Reason',
                    dataIndex: 'notes',
                    key: 'notes',
                    render: (value: string | null) => value || '—'
                  },
                  {
                    title: '',
                    key: 'actions',
                    align: 'right',
                    render: (_, row) =>
                      // Voidable only when it moved no money AND has not
                      // been voided already. The other two settlements sent
                      // cash somewhere and the service refuses them; a
                      // cancelled one has nothing left to put back. Both
                      // halves matter — offering Void on a voided note was
                      // a click that could only fail.
                      row.settlement === CreditSettlement.Reduced &&
                      row.status !== InvoiceStatus.Cancelled ? (
                        <Button
                          type="link"
                          danger
                          style={{ padding: 0 }}
                          onClick={() => onVoidCreditNote(row)}
                        >
                          Void
                        </Button>
                      ) : null
                  }
                ]}
              />
            </div>
          )}
        </Space>
      )}

      <Modal
        open={pdfOpen}
        onCancel={closePdf}
        footer={null}
        width={900}
        title="Invoice PDF"
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Invoice PDF"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        )}
      </Modal>

      <MarkPaidModal
        invoice={markPaidOpen ? invoice : null}
        onClose={() => setMarkPaidOpen(false)}
        onSuccess={reload}
      />

      {!!invoice && (
        <CreditNoteModal
          canRefundViaStripe={canRefundViaStripe}
          open={creditNoteOpen}
          invoice={invoice}
          onClose={() => setCreditNoteOpen(false)}
          onCreated={note => {
            setCreditNoteOpen(false);
            message.success(
              `Credit note ${note.invoiceNumber ?? ''} issued`.trim()
            );
            // Stay on the invoice and refresh it: the admin wants to see the
            // document they just corrected, and the note is reachable from
            // the credit-note list below.
            reload();
          }}
        />
      )}
    </PageWrapper>
  );
};

export default InvoiceDetails;
